import React, { PropsWithChildren, useEffect, useState, ChangeEvent, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { useAppState } from '../../state';
import { apiSendSms, apiSendMail, apiGetContacts } from '../../utils/api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import ManualInvite from './ManualInvite';
import { getUpn } from '../../utils';
import { RoomPrivacyType } from '../../types';

export interface Contact {
  emailaddresses: any[];
  firstname: string;
  lastname: string;
  phonenumbers: any[];
  isChecked?: boolean;
  selected?: number[];
}

interface InviteDialogInterface {
  open: boolean;
  deviceSelectionScreen?: boolean;
  onDialogClose(inviteSent: boolean): void;
  shouldRecord?: boolean;
  connect: (args: {
    privacy: RoomPrivacyType;
    roomName: string;
    shouldRecord: boolean;
    username: string;
    skipRoomCheck?: boolean;
  }) => Promise<void>;
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
  },
  headerTitle: {
    width: '50%',
    padding: '12px',
    textAlign: 'center',
    transition: 'all .3s',
    cursor: 'pointer',
    '&:not(:first-child)': {
      borderLeft: 'solid 1px #eee',
    },
    '&.selected': {
      background: '#047ac5',
      color: '#fff',
    },
  },
  contentContainer: {
    height: '350px',
    padding: '24px',
    position: 'relative',
  },
  searchInputContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  inputField: {
    fontSize: '16px',
    width: '250px',
  },
  submitBtn: {
    marginLeft: '28px',
    marginBottom: '-6px',
  },
  listContainer: {
    listStyle: 'none',
    padding: 0,
  },
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
  },
  noContacts: {
    marginTop: '2em',
  },
  userList: {
    display: 'block',
  },
  mailList: {
    padding: 0,
    marginLeft: '14px',
  },
  mailListItem: {
    padding: '4px',
  },
}));

const InviteDialog = ({
  open,
  onDialogClose,
  deviceSelectionScreen,
  connect,
  shouldRecord = false,
}: PropsWithChildren<InviteDialogInterface>) => {
  const classes = useStyles();
  const { username, domain, roomName, ownRoomDid, password } = useAppState();
  const [searchstring, setSearchstring] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [shouldDisable, setShouldDisable] = useState<boolean>(true);
  const [isManualTab, setIsManualTab] = useState<boolean>(false);
  const searchBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let disabled = !contacts.filter(contact => contact.selected && contact.selected.length).length;
    setShouldDisable(disabled);
  }, [contacts]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchstring(value);
  };

  // pressing enter for searching fires submit
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.which === 13) {
      event.stopPropagation();
      !!searchBtn.current && searchBtn.current.focus();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    // not fetching, end
    if (isFetching || !searchstring) return;
    // otherwise change status to true
    setIsFetching(true);
    // grab contacts based off searchstring
    apiGetContacts(password, username, domain, searchstring)
      .then(res => res.json())
      .then(data => {
        // end fetching since contacts are retrieved
        setIsFetching(false);
        // change searchstring back to empty
        setSearchstring('');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // let selectedContacts: Contact[] = [];
        // if (contacts.length) {
        //   // contact that is selected becomes checkmarked
        //   selectedContacts = contacts.filter(contact => contact.isChecked);
        // }
        // combines first name and last name
        setContacts(data[0].concat(data[1]).filter((contact: Contact) => contact.firstname));
      })
      .catch(e => {
        console.log(e);
        setIsFetching(false);
      });
  };

  const handleToggle = (value: Contact, index: number | null, e: ChangeEvent) => {
    e.stopPropagation();
    let contactsClone: Contact[] = JSON.parse(JSON.stringify(contacts));
    let selectedContact = contactsClone.find(
      contact => contact.firstname === value.firstname && contact.lastname === value.lastname
    );
    if (!selectedContact) return;
    selectedContact.selected = selectedContact.selected ? [...selectedContact.selected] : [];
    if (index !== null) {
      let indexInSelected = selectedContact.selected.indexOf(index);
      if (indexInSelected !== -1) {
        selectedContact.selected.splice(indexInSelected, 1);
      } else {
        selectedContact.selected.push(index);
      }
    } else {
      const isSelectAll =
        selectedContact.selected.length !== selectedContact.emailaddresses.concat(selectedContact.phonenumbers).length;
      selectedContact.selected = [];
      if (isSelectAll) {
        for (let i = 0; i < selectedContact.emailaddresses.concat(selectedContact.phonenumbers).length; i++) {
          selectedContact.selected.push(i);
        }
      }
    }
    setContacts(contactsClone);
  };

  const handleClose = (inviteSent: boolean) => {
    setIsFetching(false);
    setContacts([]);
    setSearchstring('');
    onDialogClose(inviteSent);
  };

  const handleSendInvite = () => {
    let toEmails: string[] = [];
    let toEmailNames: string[] = [];
    let toPhones: string[] = [];
    let tonames: string[] = [];
    // iterate over contacts data that invite is being sent to
    for (let i = 0; i < contacts.length; i++) {
      // set contact to each piece of info in array
      let contact = contacts[i];
      // set the right contact to selected
      let selected = contact.selected || [];
      // iterate over just selected contact's data
      for (let j = 0; j < selected.length; j++) {
        let index = selected[j];
        // emailaddress exists
        if (contact.emailaddresses[index]) {
          // add current emailaddress to empty array
          toEmails.push(contact.emailaddresses[index]);
          // add current email first and last name to empty array
          toEmailNames.push((contact.firstname ? contact.firstname : '') + (contact.lastname ? contact.lastname : ''));
        } else {
          // no email, then get rid of email address from index
          index = index - contact.emailaddresses.length;
          // add phonenumber to index
          let phoneNumber = contact.phonenumbers[index];
          // verifies number is 10 digits and adds +1 to it
          phoneNumber = phoneNumber.length === 10 ? `+1${phoneNumber}` : `+${phoneNumber}`;
          // add phonenumber and firstname/lastname to empty arrays
          toPhones.push(phoneNumber);
          tonames.push((contact.firstname ? contact.firstname : '') + (contact.lastname ? contact.lastname : ''));
        }
      }
    }
    postSendInvite(toEmails, toPhones, tonames, toEmailNames);
  };

  // sends collected contact data to invitee
  const postSendInvite = (toemails: string[], toPhones: string[], tonames: string[], toEmailNames: string[]) => {
    setIsSubmitting(true);
    const fromupn = getUpn(username, domain);
    let promises = [];
    if (toemails.length) promises.push(apiSendMail(toemails, fromupn, ownRoomDid, toEmailNames));
    if (toPhones.length) promises.push(apiSendSms(toPhones, fromupn, tonames, ownRoomDid));
    Promise.all(promises)
      .then(async () => {
        if (deviceSelectionScreen) {
          await connect({ roomName, shouldRecord, username, skipRoomCheck: true, privacy: 'public' });
          handleClose(true);
          setIsSubmitting(false);
          return;
        }
        setIsSubmitting(false);
        handleClose(true);
      })
      .catch(e => {
        setIsSubmitting(false);
        handleClose(false);
        console.log(e);
      });
  };

  return (
    <Dialog open={open} fullWidth={true} onClose={onDialogClose}>
      <div className={classes.header}>
        <Typography
          className={`${classes.headerTitle} ${isManualTab ? '' : 'selected'}`}
          variant="h6"
          onClick={() => setIsManualTab(false)}
        >
          Search
        </Typography>
        <Typography
          className={`${classes.headerTitle} ${isManualTab ? 'selected' : ''}`}
          variant="h6"
          onClick={() => setIsManualTab(true)}
        >
          Manual
        </Typography>
      </div>
      <Divider />
      {!isManualTab ? (
        <>
          <DialogContent className={classes.contentContainer}>
            <div className={classes.searchInputContainer}>
              <TextField
                id="standard-basic"
                label="Type name"
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                value={searchstring}
                className={classes.inputField}
              />
              <Button
                variant="contained"
                color="primary"
                className={classes.submitBtn}
                onClick={handleSubmit}
                disabled={!searchstring}
                ref={searchBtn}
              >
                {isFetching ? 'Searching...' : 'Search'}
              </Button>
            </div>
            {!!contacts.length && (
              <List className={classes.root}>
                {contacts.map((contact: Contact) => (
                  <ListItem
                    className={classes.userList}
                    key={contact.firstname + contact.lastname}
                    role={undefined}
                    dense
                    // button
                  >
                    <ListItemIcon>
                      <FormControlLabel
                        control={
                          <Checkbox
                            edge="start"
                            checked={
                              contact.selected
                                ? contact.selected.length === contact.emailaddresses.concat(contact.phonenumbers).length
                                : false
                            }
                            tabIndex={-1}
                            onChange={e => handleToggle(contact, null, e)}
                            color="primary"
                          />
                        }
                        label={`${contact.firstname} ${contact.lastname ? contact.lastname : ''}`}
                      />
                    </ListItemIcon>
                    <List className={classes.mailList}>
                      {contact.emailaddresses.concat(contact.phonenumbers) &&
                        contact.emailaddresses.concat(contact.phonenumbers).map((email, index) => (
                          <ListItem className={classes.mailListItem} key={email}>
                            <ListItemIcon>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    edge="start"
                                    checked={contact.selected ? contact.selected.includes(index) : false}
                                    tabIndex={-1}
                                    onChange={e => handleToggle(contact, index, e)}
                                    color="primary"
                                  />
                                }
                                label={email}
                              />
                            </ListItemIcon>
                          </ListItem>
                        ))}
                    </List>
                  </ListItem>
                ))}
              </List>
            )}
            {!contacts.length && !isFetching && (
              <Typography variant="body1" className={classes.noContacts}>
                No contacts! Search{' '}
              </Typography>
            )}
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button color="primary" variant="contained" onClick={handleSendInvite} disabled={shouldDisable}>
              {deviceSelectionScreen
                ? isSubmitting
                  ? 'Submitting...'
                  : 'Submit'
                : isSubmitting
                ? 'Sending Invite...'
                : 'Send Invite'}
            </Button>
            <Button color="secondary" variant="contained" onClick={() => handleClose(false)}>
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <ManualInvite
          propClasses={classes}
          handleClose={handleClose}
          deviceSelectionScreen={deviceSelectionScreen}
          connect={connect}
          shouldRecord={shouldRecord}
        />
      )}
    </Dialog>
  );
};

export default InviteDialog;
